// useArtifactAnalyze — per-file decomposition against the live LD endpoint (spec §4.2).
//
// Uploads ONE workflow file (Excel / Excel+VBA / Alteryx) to POST /api/logic-decomposer/analyze
// and maps the deterministic response onto an ArtifactDetail so the existing side panel renders it.
// This is the "upload a real file, see its real extracted logic" path — no DB, no LLM, no migration
// (the analyze endpoint is the live centrepiece). The Business Logic tab is fully real; Lineage /
// Overview rationale / Insights come from the heavier full-ingest pipeline and stay empty here
// (labelled honestly, never faked), so nothing is presented as computed when it isn't.

import { useCallback, useState } from 'react';
import { apiClient } from '../../api';
import type { ArtifactDetail, RegisterSignal } from './types';
import { outcomeFromRaw, OUTCOME_TO_LEGACY } from './tokens';

// Shape POST /api/logic-decomposer/analyze returns (routes/logicDecomposer.js §4.2).
interface AnalyzeResponse {
  parser: string | null;
  source_file: string;
  stats?: Record<string, unknown>;
  business_logic?: {
    counts?: {
      formulas?: number; macros?: number; hardcodes?: number; lookups?: number;
      total_statements?: number; distinct_rules?: number;
    };
    logic_rows?: { tag: 'HC' | 'FN'; location: string; code_html: string; repeat_count?: number }[];
    truncated?: number;
  };
  complexity?: Record<string, unknown>;
  // Inventory-register guard (§4; QA 2026-09-23) — present when the file is a register, not a workflow.
  register_signal?: RegisterSignal;
}

const INGEST_TIMEOUT_MS = 25 * 60 * 1000;
const POLL_INTERVAL_MS = 4000;

export interface IngestProgress {
  stage: string;
  done: number;
  total: number;
}

interface IngestJob {
  job_id: string;
  status: 'running' | 'done' | 'error';
  stage: string;
  done: number;
  total: number;
  elapsed_ms: number;
  result: IngestResponse | null;
  error: string | null;
}

const PENDING_RATIONALE =
  'Pending — this file was parsed for its logic only. AI rationale is populated by the full extraction run (or the owner questionnaire), not by the file parse.';
const PENDING_PURPOSE =
  'Pending — purpose comes from the full pipeline / questionnaire. The parse below is the file’s real extracted logic.';

/** Map the live analyze response onto the panel's ArtifactDetail. Real Business Logic; the rest
 *  stays empty (Lineage / Insights need the full ingest run) rather than being invented. */
function toArtifactDetail(res: AnalyzeResponse, id: string): ArtifactDetail {
  const bl = res.business_logic || {};
  const c = bl.counts || {};
  const rows = bl.logic_rows || [];
  const name = res.source_file || 'Uploaded file';
  return {
    id,
    name,
    owner: 'Uploaded file',
    assignee: '—',
    timing_cat: 3,
    outcome: 'queue',               // not yet dispositioned — the engine run assigns this, not the parse
    disposition: 'Queue',           // legacy alias (export vocabulary only)
    confidence: 0,
    rationale: PENDING_RATIONALE,
    purpose: PENDING_PURPOSE,
    last_modified: '—',
    usage_summary: '—',
    sources: [],
    targets: [],
    transforms: [],
    transform_count: 0,
    counts: {
      formulas: c.formulas || 0,
      macros: c.macros || 0,
      hardcodes: c.hardcodes || 0,
      lookups: c.lookups || 0,
      // Raw total, straight from the backend — NOT overwritten with logic_rows.length. The old
      // override silently changed what this field meant (raw formula/macro count vs. capped
      // distinct-row count) depending on code path, which is exactly the "150 formulas next to
      // 14 statements" mismatch QA found 2026-08-03.
      total_statements: c.total_statements || 0,
      // Falls back to rows.length only for a response from before this field existed — once
      // every server build sends it, this is just c.distinct_rules.
      distinct_rules: c.distinct_rules ?? rows.length,
    },
    logic_rows: rows,
    truncated: bl.truncated || 0,
    signals: [],
    overlaps: [],
    register_signal: res.register_signal,
  };
}

// Shape POST /api/logic-decomposer/ingest returns (the LIVE DTSE run — real lineage + disposition).
interface IngestResponse {
  source_file: string;
  run_id?: string;
  lineage?: { sources?: string[]; targets?: string[]; transforms?: { op: string; desc: string }[]; transform_count?: number };
  disposition?: {
    disposition?: string | null; target_state?: string | null; rationale?: string; purpose?: string;
    confidence?: number | null; risk_tier?: string | null; signals?: Record<string, number> | null;
  };
  business_logic?: AnalyzeResponse['business_logic'];
  translations?: ArtifactDetail['translations'] | null;
}

// Human labels for the engine's 0..1 disposition signals (scoreWorkflow §9).
const SIGNAL_LABEL: Record<string, string> = {
  platform_replaceability: 'Platform replaceability',
  criticality: 'Criticality',
  complexity: 'Complexity',
  regulatory_exposure: 'Regulatory exposure',
  blast_radius: 'Blast radius',
  overlap: 'Overlap',
  resolvability: 'Resolvability',
};

/** Map the engine's 0..1 signals onto the Insights tab's Signal tiles. */
function toSignals(sig?: Record<string, number> | null): ArtifactDetail['signals'] {
  if (!sig) return [];
  return Object.entries(sig)
    .filter(([, v]) => typeof v === 'number')
    .map(([k, v]) => ({
      label: SIGNAL_LABEL[k] || k.replace(/_/g, ' '),
      value: v.toFixed(2),
      level: (v >= 0.66 ? 'high' : v >= 0.33 ? 'medium' : 'low') as 'high' | 'medium' | 'low',
      tooltip: `${SIGNAL_LABEL[k] || k} — computed from the extraction run (0 to 1).`,
    }));
}

export const DISPOSITION_LABEL: Record<string, ArtifactDetail['disposition']> = {
  migrate: 'Migrate', document: 'Document', decommission: 'Decommission', consolidate: 'Consolidate', queue: 'Queue',
};

/** Map the LIVE ingest response onto ArtifactDetail — lineage + rationale are REAL here. */
function ingestToArtifactDetail(res: IngestResponse, id: string): ArtifactDetail {
  const base = toArtifactDetail({ parser: null, source_file: res.source_file, business_logic: res.business_logic }, id);
  const ln = res.lineage || {};
  const disp = res.disposition || {};
  return {
    ...base,
    run_id: res.run_id,
    translations: res.translations || undefined,
    outcome: outcomeFromRaw(disp.disposition),                              // single taxonomy
    disposition: OUTCOME_TO_LEGACY[outcomeFromRaw(disp.disposition)],       // legacy alias (export only)
    confidence: typeof disp.confidence === 'number' ? disp.confidence : base.confidence,
    signals: toSignals(disp.signals),
    rationale: disp.rationale || base.rationale,
    purpose: disp.purpose || base.purpose,
    target_state_label: disp.target_state ? String(disp.target_state).replace(/_/g, ' ') : undefined,
    sources: ln.sources || [],
    targets: ln.targets || [],
    transforms: ln.transforms || [],
    transform_count: ln.transform_count || (ln.transforms ? ln.transforms.length : 0),
  };
}

export interface DecomposedRun {
  run_id: string;
  source_file: string;
  parser_id: string | null;
  scored_at: string | null;
  disposition: IngestResponse['disposition'];
}

/** Every file already decomposed for this project — a run outlives the tab that made it. */
export async function listDecomposedRuns(projectId: number): Promise<DecomposedRun[]> {
  const res = await apiClient.get(`/api/logic-decomposer/runs?projectId=${projectId}`);
  return (res.data as { items?: DecomposedRun[] }).items || [];
}

/** Re-open one decomposed run: lineage + disposition from the database, logic re-parsed. */
export async function loadDecomposedRun(runId: string, projectId: number): Promise<ArtifactDetail> {
  const res = await apiClient.get(`/api/logic-decomposer/runs/${runId}?projectId=${projectId}`);
  return ingestToArtifactDetail(res.data as IngestResponse, `run:${runId}`);
}

export interface UseArtifactAnalyze {
  analyzing: boolean;
  error: string | null;
  /** Live stage of the running extraction, or null when idle. */
  progress: IngestProgress | null;
  /** Upload + analyze one file. Returns the panel-ready detail, or null on failure (error is set). */
  analyze: (file: File) => Promise<ArtifactDetail | null>;
  /** LIVE decompose via the real DTSE engine (needs a project + LDX_REAL_INGEST on the server).
   *  Throws on 501/failure so the caller can fall back to analyze(). */
  ingest: (file: File, projectId: number, selectedModelId?: string | null) => Promise<ArtifactDetail>;
}

export function useArtifactAnalyze(): UseArtifactAnalyze {
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<IngestProgress | null>(null);

  const analyze = useCallback(async (file: File): Promise<ArtifactDetail | null> => {
    setAnalyzing(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await apiClient.post('/api/logic-decomposer/analyze', form);
      // Stable, unique id so re-uploading the same filename opens a fresh panel entry.
      const id = `analyzed:${file.name}:${Date.now()}`;
      return toArtifactDetail(res.data as AnalyzeResponse, id);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setError(err?.response?.data?.error || err?.message || 'Could not analyze the file.');
      return null;
    } finally {
      setAnalyzing(false);
    }
  }, []);

  // The run is started server-side and polled: a full extraction is dozens of model calls drained
  // one at a time, which outlives any HTTP timeout. POST returns a job id immediately.
  const ingest = useCallback(async (file: File, projectId: number, selectedModelId: string | null = null): Promise<ArtifactDetail> => {
    setAnalyzing(true);
    setError(null);
    setProgress(null);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('projectId', String(projectId));
      if (selectedModelId) form.append('selectedModelId', selectedModelId);
      const started = await apiClient.post('/api/logic-decomposer/ingest', form);
      const jobId = (started.data as { job_id?: string }).job_id;
      if (!jobId) throw new Error('The server did not start a decomposition job.');

      const deadline = Date.now() + INGEST_TIMEOUT_MS;
      for (;;) {
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
        const res = await apiClient.get(`/api/logic-decomposer/ingest/${jobId}`);
        const job = res.data as IngestJob;
        setProgress({ stage: job.stage, done: job.done, total: job.total });
        if (job.status === 'done' && job.result) {
          const id = `analyzed:${file.name}:${Date.now()}`;
          return ingestToArtifactDetail(job.result, id);
        }
        if (job.status === 'error') throw new Error(job.error || 'Decomposition failed.');
        if (Date.now() > deadline) throw new Error('The decomposition run is taking longer than expected — it is still running on the server.');
      }
    } finally {
      setAnalyzing(false);
      setProgress(null);
    }
  }, []);

  return { analyzing, error, progress, analyze, ingest };
}
